import isNil from 'lodash/isNil'
import type { FunctionComponent, ReactNode } from 'react'
import React from 'react'
import type { ModalProps as ModalPropsCore } from 'react-bootstrap'
import { Modal as ModalCore } from 'react-bootstrap'
import { BusyButton } from '@ztechapps/webui.button'
import styles from './index.module.scss'
import { DraggableModal } from './DraggableModal'
import { useContext as useContextCore } from 'react'
import isFunction from 'lodash/isFunction'
import { ModalDragHandle } from '../DragHandle'
import { useStickyModalSize } from './useStickyModalSize'
import { useFocusTrap } from '../useFocusTrap'

export type ModalTriggerProps = { onClick: () => void; icon?: string }
export type ModalProps = Omit<ModalPropsCore, 'size'> & {
  show?: boolean
  backdrop?: boolean | 'static'
  trigger?: FunctionComponent<ModalTriggerProps>
  triggerIcon?: string
  children?: ReactNode | ((args?: ModalChildrenProps) => ReactNode)
  size?: 'sm' | 'lg' | 'xl' | '90' | '75' | '50' | '25' | 'md'
  triggerButtonText?: string
  /**
   * Opt the modal dialog into user-driven resizing. When `true`, the dialog gets a
   * bottom-right resize handle (native CSS `resize: both`). The initial size still comes from
   * `size` / `dialogClassName`; the user's drag overrides those via inline `width` / `height`.
   * Min/max bounds keep the dialog inside the viewport.
   */
  resizable?: boolean
  /**
   * When set together with `resizable`, persists the user-resized width/height to localStorage
   * under this key. Subsequent opens restore the saved size. Without this prop, the dialog
   * resets to its default size on each open. Key collides at the global localStorage namespace,
   * so use a feature-scoped value (e.g. `'spec-grid-maximize'`, `'document-preview'`).
   */
  resizeStorageKey?: string
}

export type ModalChildrenProps = {
  show?: boolean
  showModal?: boolean
  toggleShowModal?: () => void
}

export const Modal = ({
  size = 'lg',
  show,
  onHide,
  backdrop = 'static',
  trigger,
  triggerIcon = 'ellipsis-h',
  children,
  dialogClassName,
  triggerButtonText = 'Open',
  enforceFocus = false,
  resizable = false,
  resizeStorageKey,
  scrollable = false,
  ...rest
}: ModalProps) => {
  const [showModal, setShowModal] = React.useState(!!show)
  const modalHandleRef = React.useRef<{ dialog: HTMLElement | null }>(null)
  useFocusTrap({ getContainer: () => modalHandleRef.current?.dialog, isActive: showModal })

  // Absolute setters, not a relative toggle: the `show` prop sync below and every close
  // action (backdrop/ESC, FormModal's afterSubmit/afterReset) both drive this SAME state, so a
  // relative `setShowModal(e => !e)` can flip a just-closed `false` back to `true` when it lands
  // after the prop sync. Two absolute intents (open/close) can never contradict each other.
  const openModal = () => setShowModal(true)
  const closeModal = () => setShowModal(false)

  // Closing runs the modal's own close AND the caller's `onHide`, so an explicit `onHide: undefined` no longer disables Esc / the header close button.
  const handleHide = () => {
    closeModal()
    if (isFunction(onHide)) onHide()
  }

  React.useEffect(() => {
    if (!isNil(show)) {
      setShowModal(show)
    }
  }, [show])

  //run the trigger, or use hte default trigger
  const Trigger = trigger
  const triggerNode = Trigger ? (
    <Trigger onClick={openModal} icon={triggerIcon} />
  ) : (
    <BusyButton {...{ icon: triggerIcon, onClick: openModal, text: triggerButtonText }} />
  )

  let newSize: any = 'lg'
  if (['90', '75', '50', '25'].includes(size)) {
    dialogClassName = `${dialogClassName ?? ''} ${styles[`modal${size}w`]}`
    newSize = 'lg'
  } else {
    newSize = size
  }

  if (resizable) {
    dialogClassName = `${dialogClassName ?? ''} ${styles.resizable}`
  }

  useStickyModalSize(resizeStorageKey, showModal, resizable ? styles.resizable : '')

  return (
    <React.Fragment>
      {triggerNode}
      <ModalCore
        animation={true}
        {...{
          show: showModal,
          onHide: handleHide,
          backdrop,
          enforceFocus,
          ...rest,
        }}
        size={newSize}
        // a resizable dialog keeps its header and footer pinned: Bootstrap's `.modal-dialog-scrollable` scrolls the body
        scrollable={scrollable || resizable}
        dialogClassName={dialogClassName}
        dialogAs={DraggableModal}
        // react-bootstrap types this ref as a `div` ref (node_modules/react-bootstrap/esm/Modal.d.ts)
        // but forwards it to @restart/ui's Modal, which fills it with its ModalHandle whose
        // `dialog` is the `role="dialog"` element (node_modules/react-bootstrap/cjs/Modal.js:87,224;
        // node_modules/@restart/ui/cjs/Modal.js:44-58,100,200-209).
        ref={modalHandleRef as unknown as React.Ref<HTMLDivElement>}
      >
        <ModalProvider toggleShowModal={closeModal} show={showModal}>
          {isFunction(children) ? children?.({ show, showModal, toggleShowModal: closeModal }) : children}
        </ModalProvider>
      </ModalCore>
    </React.Fragment>
  )
}

export type ModalProviderProps = {
  toggleShowModal: () => void
  show: boolean
  children: React.ReactNode
}

export type ModalContext = Omit<ModalProviderProps, 'children'>

const context = React.createContext<ModalContext>({} as ModalContext)
const CoreProvider = context.Provider

export const ModalProvider = ({ children, ...props }: ModalProviderProps) => {
  return <CoreProvider value={{ ...props }}>{children}</CoreProvider>
}

export const useModalContext = () => useContextCore(context)

///add the modal quick links for bootstrap Footer and Header and Body
Modal.Body = ModalCore.Body
Modal.Footer = ModalCore.Footer
Modal.Header = ModalCore.Header
Modal.DragHandle = ModalDragHandle
